import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useState,
} from 'react';
import {
  matchRoutes,
  useLocation,
  useNavigate,
  useParams,
  useResolvedPath,
  useRoutes,
  useSearchParams,
} from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import {
  ButtonIcon,
  FullPage,
  Header,
  type HeaderMetadataItem,
  Link,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { RiStarFill, RiStarLine } from '@remixicon/react';
import {
  type CompoundEntityRef,
  DEFAULT_NAMESPACE,
  type Entity,
  RELATION_OWNED_BY,
} from '@backstage/catalog-model';
import {
  Content,
  Link as LegacyLink,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import {
  configApiRef,
  useApi,
  useElementFilter,
  useRouteRef,
  useRouteRefParams,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  EntityLayout as CatalogEntityLayout,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogTranslationRef } from '@backstage/plugin-catalog/alpha';
import {
  catalogReactTranslationRef,
  entityRouteRef,
  getEntityRelations,
  InspectEntityDialog,
  UnregisterEntityDialog,
  useAsyncEntity,
  useEntityPresentation,
  useEntityRefLink,
  useStarredEntity,
} from '@backstage/plugin-catalog-react';
import { EntityContextMenu } from './EntityContextMenu';

/**
 * The component data key that @backstage/plugin-catalog attaches to
 * `EntityLayout.Route`. Selecting children by it keeps the `EntityLayout.Route`
 * elements, their paths, titles and `if` conditions exactly as upstream reads
 * them.
 */
const ENTITY_LAYOUT_ROUTE_KEY = 'plugin.catalog.entityLayoutRoute';

type InspectTab = NonNullable<
  ComponentProps<typeof InspectEntityDialog>['initialTab']
>;

type EntityLayoutRoute = {
  path: string;
  title: string;
  children: JSX.Element;
};

/**
 * The title and the kind/type line, worded as in upstream's legacy
 * EntityLayout: "<title or name> in <namespace>" outside the default
 * namespace, and the lower-cased kind and spec.type.
 */
const headerText = (
  params: { kind?: string; namespace?: string; name?: string },
  entity: Entity | undefined,
) => {
  const kind = params.kind ?? entity?.kind ?? '';
  const namespace = params.namespace ?? entity?.metadata.namespace ?? '';
  const name =
    entity?.metadata.title ?? params.name ?? entity?.metadata.name ?? '';
  const type = entity?.spec?.type;
  return {
    title: `${name}${
      namespace && namespace !== DEFAULT_NAMESPACE ? ` in ${namespace}` : ''
    }`,
    tags: [kind, ...(typeof type === 'string' ? [type] : [])]
      .filter(Boolean)
      .map(label => ({ label: label.toLowerCase() })),
  };
};

/**
 * The tab for the path below the entity URL, matched like core-components'
 * RoutedTabs: the most specific tab path wins, and an unknown path shows the
 * first tab.
 */
const useSelectedRoute = (routes: EntityLayoutRoute[]) => {
  const params = useParams();
  const sortedRoutes = routes
    .map(({ path, children }) => ({
      caseSensitive: false,
      path: `${path}/*`,
      element: children,
    }))
    .sort((a, b) =>
      b.path.replace(/\/\*$/, '').localeCompare(a.path.replace(/\/\*$/, '')),
    );
  const element = useRoutes(sortedRoutes) ?? routes[0]?.children;
  let currentPath = params['*'] ?? '';
  if (!currentPath.startsWith('/')) {
    currentPath = `/${currentPath}`;
  }
  const [match] = matchRoutes(sortedRoutes, currentPath) ?? [];
  const route =
    (match && routes.find(r => `${r.path}/*` === match.route.path)) ??
    routes[0];
  return { route, element };
};

/**
 * Sets the browser-tab title as core-components' Header and RoutedTabs did
 * ("<entity> | <tab> | Make Backstage"); BUI's Header does not set it.
 */
const usePageTitle = (title: string, tabTitle: string | undefined) => {
  const appTitle =
    useApi(configApiRef).getOptionalString('app.title') ?? 'Backstage';
  useEffect(() => {
    const previous = document.title;
    document.title = [title, tabTitle, appTitle].filter(Boolean).join(' | ');
    return () => {
      document.title = previous;
    };
  }, [title, tabTitle, appTitle]);
};

const OwnerLink = ({ entityRef }: { entityRef: CompoundEntityRef }) => {
  const entityLink = useEntityRefLink();
  const { primaryTitle, Icon } = useEntityPresentation(entityRef, {
    defaultKind: 'Group',
  });
  return (
    // Brand-coloured, as the legacy header's owner link was.
    <Link
      href={entityLink(entityRef)}
      variant="body-medium"
      style={{ color: 'var(--make-fg-brand)' }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--bui-space-1)',
        }}
      >
        {Icon && <Icon fontSize="inherit" />}
        {primaryTitle}
      </span>
    </Link>
  );
};

const useMetadata = (entity: Entity | undefined): HeaderMetadataItem[] => {
  const { t } = useTranslationRef(catalogTranslationRef);
  if (!entity) {
    return [];
  }
  const metadata: HeaderMetadataItem[] = [];
  const owners = getEntityRelations(entity, RELATION_OWNED_BY);
  if (owners.length > 0) {
    metadata.push({
      label: t('entityLabels.ownerLabel'),
      value: owners.map((owner, index) => (
        <span key={`${owner.kind}:${owner.namespace}/${owner.name}`}>
          {index > 0 && ', '}
          <OwnerLink entityRef={owner} />
        </span>
      )),
    });
  }
  const lifecycle = entity.spec?.lifecycle;
  if (lifecycle) {
    metadata.push({
      label: t('entityLabels.lifecycleLabel'),
      value: lifecycle.toString(),
    });
  }
  return metadata;
};

const FavoriteEntityButton = ({ entity }: { entity: Entity }) => {
  const { t } = useTranslationRef(catalogReactTranslationRef);
  const { isStarredEntity, toggleStarredEntity } = useStarredEntity(entity);
  const label = isStarredEntity
    ? t('favoriteEntity.removeFromFavorites')
    : t('favoriteEntity.addToFavorites');
  return (
    <TooltipTrigger>
      <ButtonIcon
        variant="secondary"
        aria-label={label}
        icon={isStarredEntity ? <RiStarFill /> : <RiStarLine />}
        onPress={toggleStarredEntity}
      />
      <Tooltip>{label}</Tooltip>
    </TooltipTrigger>
  );
};

type EntityLayoutProps = {
  children?: ReactNode;
};

/**
 * Make's entity page layout: a BUI Header with the entity's kind and type,
 * title, Owner and Lifecycle, favourite toggle, "More actions" menu and tabs,
 * replacing the Material UI header and tabs of @backstage/plugin-catalog's
 * legacy EntityLayout.
 *
 * Tabs are still declared with upstream's `EntityLayout.Route`, so their
 * paths, titles and `if` conditions, and the mount points that the app's
 * route discovery collects from them, are unchanged. Tab content renders in
 * the same core-components Content as before.
 */
export const EntityLayout = ({ children }: EntityLayoutProps) => {
  const routeParams = useRouteRefParams(entityRouteRef);
  const { entity, loading, error } = useAsyncEntity();
  const { t } = useTranslationRef(catalogTranslationRef);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const catalogRoute = useRouteRef(catalogPlugin.routes.catalogIndex);
  const unregisterRedirectRoute = useRouteRef(
    catalogPlugin.externalRoutes.unregisterRedirect,
  );
  const basePath = useResolvedPath('.').pathname.replace(/\/$/, '');
  const [unregisterDialogOpen, setUnregisterDialogOpen] = useState(false);

  const routes = useElementFilter(
    children,
    elements =>
      elements
        .selectByComponentData({
          key: ENTITY_LAYOUT_ROUTE_KEY,
          withStrictError:
            'Child of EntityLayout must be an EntityLayout.Route',
        })
        .getElements<{
          path: string;
          title: string;
          children: JSX.Element;
          if?: (entity: Entity) => boolean;
        }>()
        .flatMap(({ props }) => {
          if (!entity || (props.if && !props.if(entity))) {
            return [];
          }
          return [
            { path: props.path, title: props.title, children: props.children },
          ];
        }),
    [entity],
  );
  const { route, element } = useSelectedRoute(routes);
  const { title, tags } = headerText(routeParams, entity);
  const metadata = useMetadata(entity);
  usePageTitle(title, entity ? route?.title : undefined);

  useEffect(() => {
    setUnregisterDialogOpen(false);
  }, [location.pathname]);

  const setInspectTab = (tab: string | undefined) =>
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      if (tab === undefined) {
        next.delete('inspect');
      } else {
        next.set('inspect', tab);
      }
      return next;
    });
  const inspectTab = searchParams.get('inspect');

  return (
    <FullPage>
      <Header
        title={title}
        tags={tags}
        metadata={metadata}
        tabs={
          entity
            ? routes.map(r => ({
                id: r.path,
                label: r.title,
                href: r.path === '/' ? basePath : `${basePath}${r.path}`,
              }))
            : undefined
        }
        activeTabId={route?.path}
        customActions={
          entity && (
            <>
              <FavoriteEntityButton entity={entity} />
              <EntityContextMenu
                onUnregisterEntity={() => setUnregisterDialogOpen(true)}
                onInspectEntity={() => setInspectTab('')}
              />
            </>
          )
        }
      />
      {loading && <Progress />}
      {entity && <Content>{element}</Content>}
      {error && (
        <Content>
          <Alert severity="error">{error.toString()}</Alert>
        </Content>
      )}
      {!loading && !error && !entity && (
        <Content>
          <WarningPanel title={t('entityLabels.warningPanelTitle')}>
            {t('entityPage.notFoundMessage', {
              kind: routeParams.kind,
              link: (
                <LegacyLink to="https://backstage.io/docs/features/software-catalog/references">
                  {t('entityPage.notFoundLinkText')}
                </LegacyLink>
              ),
            })}
          </WarningPanel>
        </Content>
      )}
      {entity && typeof inspectTab === 'string' && (
        <InspectEntityDialog
          entity={entity}
          initialTab={(inspectTab as InspectTab) || undefined}
          onSelect={setInspectTab}
          open
          onClose={() => setInspectTab(undefined)}
        />
      )}
      {entity && (
        <UnregisterEntityDialog
          open={unregisterDialogOpen}
          entity={entity}
          onConfirm={() => {
            setUnregisterDialogOpen(false);
            navigate(
              unregisterRedirectRoute
                ? unregisterRedirectRoute()
                : catalogRoute(),
            );
          }}
          onClose={() => setUnregisterDialogOpen(false)}
        />
      )}
    </FullPage>
  );
};

EntityLayout.Route = CatalogEntityLayout.Route;
